import classNames from 'classnames';
import { Divider } from '@/shared/components/Divider';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import useTokenPrice from '@/shared/hooks/useTokenPrice';
import ArrowIcon from '@/shared/icons/flip-ui-kit/small/Arrow';
import DollarIcon from '@/shared/icons/flip-ui-kit/small/Dollar';
import MoneyBagIcon from '@/shared/icons/flip-ui-kit/small/MoneyBag';
import PercentageIcon from '@/shared/icons/flip-ui-kit/small/Percentage';
import { apyToText } from '@/shared/utils/boost';
import { chainflipAssetMap } from '@/shared/utils/env';
import { formatUsdValue } from '@/shared/utils/numbers';
import {
  TOOLTIP_COPY,
  type TradingStrategy,
  calculateAnnualReturn,
  convertedTick,
  isAllowedStrategyType,
  templateKeySchema,
} from '@/shared/utils/strategies';
import type TokenAmount from '@/shared/utils/TokenAmount';

export const StrategyOverview = ({
  strategy,
  apyPercent,
  newBaseAssetAmount,
  newQuoteAssetAmount,
}: {
  strategy: Pick<TradingStrategy, 'baseLiquidity' | 'quoteLiquidity' | 'templateKey'>;
  apyPercent: number | undefined;
  newBaseAssetAmount?: TokenAmount;
  newQuoteAssetAmount?: TokenAmount;
}) => {
  const { baseLiquidity, quoteLiquidity, templateKey } = strategy;

  const { minBuyTick, maxBuyTick, minSellTick, maxSellTick, baseAsset, quoteAsset, type } =
    templateKeySchema.parse(templateKey);

  const isLiquidityChanged =
    (newBaseAssetAmount && !baseLiquidity.eq(newBaseAssetAmount)) ||
    (newQuoteAssetAmount && !quoteLiquidity.eq(newQuoteAssetAmount));
  const isStrategyClosing =
    isLiquidityChanged && newBaseAssetAmount?.eq(0) && newQuoteAssetAmount?.eq(0);

  const { price: baseAssetPrice } = useTokenPrice(chainflipAssetMap[baseAsset]);
  const { price: quoteAssetPrice } = useTokenPrice(chainflipAssetMap[quoteAsset]);

  const baseAssetValueUsd = baseLiquidity.mul(baseAssetPrice ?? 0).toNumber();
  const quoteAssetValueUsd = quoteLiquidity?.mul(quoteAssetPrice ?? 0).toNumber();
  const totalLiquidityUsd = baseAssetValueUsd + quoteAssetValueUsd;
  const projectedAnnualReturnValueUsd =
    apyPercent && calculateAnnualReturn(totalLiquidityUsd, apyPercent);

  const newBaseAssetValueUsd =
    (newBaseAssetAmount ?? baseLiquidity).mul(baseAssetPrice ?? 0).toNumber() ?? 0;
  const newQuoteAssetValueUsd =
    (newQuoteAssetAmount ?? quoteLiquidity).mul(quoteAssetPrice ?? 0).toNumber() ?? 0;
  const newTotalLiquidityUsd = newBaseAssetValueUsd + newQuoteAssetValueUsd;
  const newProjectedAnnualReturnValueUsd =
    apyPercent && calculateAnnualReturn(newTotalLiquidityUsd, apyPercent);

  const label = type === 'ORACLE_TRACKING' ? 'Offset' : 'Price';

  return (
    <div className="flex flex-col gap-y-3 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium text-14 text-cf-light-2 shadow-grayPop3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-1">
          <DollarIcon />
          <span>Total USD Value</span>
          <QuestionMarkTooltip content="The total value (in USD) for this strategy." />
        </div>
        <div className="flex items-center gap-x-2 ">
          <span className={classNames(!isLiquidityChanged && 'text-cf-white')}>
            {formatUsdValue(totalLiquidityUsd)}
          </span>
          {isLiquidityChanged && (
            <>
              <ArrowIcon />
              <span className="text-cf-white">{formatUsdValue(newTotalLiquidityUsd)}</span>
            </>
          )}
        </div>
      </div>
      <Divider className="h-px" />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-1">
          <ArrowIcon direction="down" />
          <span>Buy {label}</span>
          {isAllowedStrategyType(type) && <QuestionMarkTooltip content={TOOLTIP_COPY[type].buy} />}
        </div>
        <div className="flex items-center gap-x-1 text-cf-white">
          {convertedTick(minBuyTick, maxBuyTick, baseAsset, type)}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-1">
          <ArrowIcon direction="up" />
          <span>Sell {label}</span>
          {isAllowedStrategyType(type) && <QuestionMarkTooltip content={TOOLTIP_COPY[type].sell} />}
        </div>
        <div className="flex items-center gap-x-1 text-cf-white">
          {convertedTick(minSellTick, maxSellTick, baseAsset, type)}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-1">
          <PercentageIcon />
          <span>Estimated APY</span>
          <QuestionMarkTooltip content="Estimated annual yield projected from recent fill data and allocation." />
        </div>
        {apyPercent !== undefined ? (
          <div className="flex items-center gap-x-2 ">
            <span className={classNames(!isStrategyClosing && 'text-cf-white')}>
              {apyToText(apyPercent)}
            </span>
            {isStrategyClosing && (
              <>
                <ArrowIcon />
                <span className="text-cf-white">{apyToText(0)}</span>
              </>
            )}
          </div>
        ) : (
          <div>N/A</div>
        )}
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-1">
          <MoneyBagIcon />
          <span>Estimated Annual Return</span>
          <QuestionMarkTooltip content="Estimated annual earnings (in USD) projected from recent fill data and allocation." />
        </div>
        {projectedAnnualReturnValueUsd !== undefined ? (
          <div className="flex items-center gap-x-2 ">
            <span
              className={classNames(
                !isLiquidityChanged &&
                  (projectedAnnualReturnValueUsd > 0 ? 'text-cf-green-3' : 'text-cf-white'),
              )}
            >
              {formatUsdValue(projectedAnnualReturnValueUsd, undefined, true)}
            </span>
            {isLiquidityChanged && newProjectedAnnualReturnValueUsd !== undefined && (
              <>
                <ArrowIcon />
                <span
                  className={classNames(
                    newProjectedAnnualReturnValueUsd > 0 ? 'text-cf-green-3' : 'text-cf-white',
                  )}
                >
                  {formatUsdValue(newProjectedAnnualReturnValueUsd, undefined, true)}
                </span>
              </>
            )}
          </div>
        ) : (
          <div>N/A</div>
        )}
      </div>
    </div>
  );
};
